import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Receipt, PaperPlaneRight, FilePdf } from '@phosphor-icons/react';
import ArticleSelector from '../components/ArticleSelector';
import VatRateSelect from '../components/VatRateSelect';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// French labels for payment methods — used in the list and elsewhere. Same keys as the backend accepts.
const PAYMENT_LABELS = {
  cash: 'Espèces',
  card: 'Carte bancaire',
  transfer: 'Virement',
  check: 'Chèque',
  other: 'Autre',
};

const CounterSales = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [keys, setKeys] = useState([]);
  const [itProducts, setItProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [printing, setPrinting] = useState([]);
  const [businessCards, setBusinessCards] = useState([]);
  const [binding, setBinding] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saleForm, setSaleForm] = useState({ items: [], notes: '', payment_method: 'cash' });
  const [selectedProductKey, setSelectedProductKey] = useState('');
  const [currentQty, setCurrentQty] = useState('1');
  // 'product' = pick from catalog, 'custom' = free-form article (e.g. one-off service)
  const [itemMode, setItemMode] = useState('product');
  const [customItem, setCustomItem] = useState({ type: '', description: '', quantity: '1', unit_price: '', vat_rate: 0.085 });
  const [sessionOpen, setSessionOpen] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, k, i, sv, pr, bc, bi, ps, sess] = await Promise.all([
        axios.get(`${API_URL}/api/counter-sales`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/keys`, { withCredentials: true }),
        axios.get(`${API_URL}/api/it-products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/services`, { withCredentials: true }),
        axios.get(`${API_URL}/api/printing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/business-cards`, { withCredentials: true }),
        axios.get(`${API_URL}/api/binding`, { withCredentials: true }),
        axios.get(`${API_URL}/api/prestations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/cash/sessions/current`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setSales(s.data);
      setKeys(k.data);
      setItProducts(i.data);
      setServices(sv.data);
      setPrinting(pr.data);
      setBusinessCards(bc.data);
      setBinding(bi.data);
      setPrestations(ps.data);
      setSessionOpen(sess.data && sess.data.open ? sess.data.session : null);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSaleForm({ items: [], notes: '', payment_method: 'cash' });
    setSelectedProductKey('');
    setCurrentQty('1');
    setItemMode('product');
    setCustomItem({ type: '', description: '', quantity: '1', unit_price: '', vat_rate: 0.085 });
  };

  const addCustomItem = () => {
    if (!customItem.type || !customItem.description || !customItem.unit_price) {
      return toast.error('Renseignez type, description et prix');
    }
    const qty = parseInt(customItem.quantity) || 1;
    const vat = parseFloat(customItem.vat_rate) || 0;
    // Custom price is TTC by convention (matches "Article personnalisé" on factures).
    // Convert to HT for storage — the totals row will reconstruct TTC via vat_rate.
    const ttcUnit = parseFloat(customItem.unit_price) || 0;
    const unitHT = vat > 0 ? +(ttcUnit / (1 + vat)).toFixed(4) : ttcUnit;
    setSaleForm({
      ...saleForm,
      items: [...saleForm.items, {
        type: customItem.type,
        description: customItem.description,
        quantity: qty,
        unit_price: unitHT,
        total_price: qty * unitHT,
        product_id: null,
        product_type: null,
        vat_rate: vat,
        discount_percent: 0,
      }],
    });
    setCustomItem({ type: '', description: '', quantity: '1', unit_price: '', vat_rate: 0.085 });
  };

  const addItem = () => {
    if (!selectedProductKey) return toast.error('Sélectionnez un article');
    const [productType, productId] = selectedProductKey.split(':');
    const pool = { key: keys, it_product: itProducts, service: services, printing, business_card: businessCards, binding, prestation: prestations };
    const arr = pool[productType] || [];
    const product = arr.find((p) => p.id === productId);
    if (!product) return toast.error('Produit introuvable');
    const quantity = parseInt(currentQty) || 1;
    const vat = product.vat_rate !== undefined ? product.vat_rate : 0.085;
    const toHT = (ttc) => (vat > 0 ? +(ttc / (1 + vat)).toFixed(4) : ttc);
    let unitPrice; let typeLabel; let descLabel;
    if (productType === 'service') {
      unitPrice = toHT(product.unit_price);
      typeLabel = product.category || 'Service';
      descLabel = product.name + (product.description ? ` - ${product.description}` : '');
    } else if (productType === 'printing') {
      unitPrice = toHT(product.price);
      typeLabel = 'Impression';
      descLabel = `Impression ${product.format} - ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
    } else if (productType === 'business_card') {
      unitPrice = toHT(product.price);
      typeLabel = 'Carte de visite';
      descLabel = `Cartes de visite - Papier ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
    } else if (productType === 'binding') {
      unitPrice = toHT(product.price);
      typeLabel = 'Reliure';
      descLabel = `Reliure ${product.min_pages} à ${product.max_pages} pages`;
    } else if (productType === 'prestation') {
      unitPrice = toHT(product.price);
      typeLabel = 'Prestation';
      descLabel = product.name;
    } else {
      unitPrice = toHT(product.sale_price);
      typeLabel = productType === 'key' ? 'Clé' : 'Produit IT';
      descLabel = product.name;
      if (productType === 'key' && product.characteristics) descLabel += ` - ${product.characteristics}`;
    }
    setSaleForm({
      ...saleForm,
      items: [...saleForm.items, {
        type: typeLabel, description: descLabel, quantity,
        unit_price: unitPrice, total_price: quantity * unitPrice,
        product_id: productId, product_type: productType,
        vat_rate: vat, discount_percent: 0,
      }],
    });
    setSelectedProductKey('');
    setCurrentQty('1');
  };

  const removeItem = (idx) => setSaleForm({ ...saleForm, items: saleForm.items.filter((_, i) => i !== idx) });

  const updateDiscount = (idx, value) => {
    const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
    setSaleForm({
      ...saleForm,
      items: saleForm.items.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          discount_percent: pct,
          discount_type: pct > 0 ? 'percent' : null,
          discount_value: pct,
          total_price: (it.quantity || 0) * (it.unit_price || 0) * (1 - pct / 100),
        };
      }),
    });
  };

  const totals = saleForm.items.reduce(
    (acc, it) => {
      const qty = parseFloat(it.quantity) || 0;
      const unit = parseFloat(it.unit_price) || 0;
      const pct = Math.max(0, Math.min(100, parseFloat(it.discount_percent) || 0));
      const vat = parseFloat(it.vat_rate) || 0;
      const ht = qty * unit * (1 - pct / 100);
      acc.ht += ht;
      acc.vat += ht * vat;
      return acc;
    },
    { ht: 0, vat: 0 },
  );
  const totalTTC = totals.ht + totals.vat;
  const displayVat = saleForm.items.length ? (parseFloat(saleForm.items[0].vat_rate) || 0.085) : 0.085;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (saleForm.items.length === 0) return toast.error('Ajoutez au moins un article');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/counter-sales`, saleForm, { withCredentials: true });
      toast.success(`Vente ${r.data.sale_number} enregistrée`);
      // Fetch ticket PDF as blob and open in a new tab so the browser print dialog can be used
      const pdfResp = await axios.get(`${API_URL}/api/counter-sales/${r.data.id}/ticket`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setShowCreate(false);
      resetForm();
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'encaissement", { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = async (saleId, saleNumber) => {
    try {
      const pdfResp = await axios.get(`${API_URL}/api/counter-sales/${saleId}/ticket`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Erreur lors du téléchargement du ticket');
    }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="counter-sales-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Vente Comptoir</h1>
            <p className="text-slate-600">Encaissez vos ventes rapides sans facture — ticket de caisse imprimable.</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowCreate(true); }}
            data-testid="new-counter-sale-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            disabled={!sessionOpen}
            title={!sessionOpen ? 'Ouvrez une session de caisse pour enregistrer une vente' : undefined}
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Nouvelle vente comptoir
          </Button>
        </div>

        {!sessionOpen && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="session-warning">
            ⚠️ Aucune session de caisse ouverte. Ouvrez une session dans le module <strong>Caisse</strong> avant d&apos;enregistrer une vente comptoir.
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Chargement…</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">N°</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant TTC</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Paiement</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Émis par</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`counter-sale-row-${s.id}`}>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900">{s.sale_number}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{new Date(s.created_at).toLocaleString('fr-FR')}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right font-medium">{s.total_amount.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{PAYMENT_LABELS[s.payment_method] || s.payment_method}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{s.issued_by}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openTicket(s.id, s.sale_number)}
                          data-testid={`reprint-ticket-${s.id}`}
                          className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md"
                          title="Réimprimer le ticket"
                        >
                          <FilePdf size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-slate-500">Aucune vente comptoir enregistrée.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-counter-sale-dialog">
            <DialogHeader>
              <DialogTitle>Nouvelle vente comptoir</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Articles</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setItemMode('product')}
                      data-testid="counter-item-mode-product-btn"
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'product'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      Produit en stock
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemMode('custom')}
                      data-testid="counter-item-mode-custom-btn"
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'custom'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      Article personnalisé
                    </button>
                  </div>
                </div>

                {itemMode === 'product' ? (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-9">
                      <ArticleSelector
                        value={selectedProductKey}
                        onValueChange={setSelectedProductKey}
                        keys={keys}
                        itProducts={itProducts}
                        services={services}
                        printing={printing}
                        businessCards={businessCards}
                        binding={binding}
                        prestations={prestations}
                        testId="counter-sale-product-select"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" min="1" value={currentQty} onChange={(e) => setCurrentQty(e.target.value)} placeholder="Qté" data-testid="counter-item-quantity-input" />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" onClick={addItem} data-testid="counter-add-item-btn" className="bg-blue-600 hover:bg-blue-500 text-white w-full">
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-2">
                      <Input placeholder="Type" value={customItem.type} onChange={(e) => setCustomItem({ ...customItem, type: e.target.value })} data-testid="counter-custom-type-input" />
                    </div>
                    <div className="col-span-3">
                      <Input placeholder="Description" value={customItem.description} onChange={(e) => setCustomItem({ ...customItem, description: e.target.value })} data-testid="counter-custom-description-input" />
                    </div>
                    <div className="col-span-1">
                      <Input type="number" min="1" placeholder="Qté" value={customItem.quantity} onChange={(e) => setCustomItem({ ...customItem, quantity: e.target.value })} data-testid="counter-custom-quantity-input" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" step="0.01" placeholder="Prix TTC" value={customItem.unit_price} onChange={(e) => setCustomItem({ ...customItem, unit_price: e.target.value })} data-testid="counter-custom-price-input" />
                    </div>
                    <div className="col-span-2">
                      <VatRateSelect value={customItem.vat_rate} onChange={(v) => setCustomItem({ ...customItem, vat_rate: v })} testId="counter-custom-vat-select" />
                    </div>
                    <div className="col-span-2">
                      <Button type="button" onClick={addCustomItem} data-testid="counter-add-custom-btn" className="bg-blue-600 hover:bg-blue-500 text-white w-full">
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  💡 Le stock est décrémenté pour les produits catalogue. Utilisez &laquo; Article personnalisé &raquo; pour les ventes ponctuelles.
                </p>
              </div>

              {saleForm.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-xs uppercase text-slate-600">Description</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Qté</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Remise %</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT remisé</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Total HT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {saleForm.items.map((it, idx) => {
                        const pct = Math.max(0, Math.min(100, parseFloat(it.discount_percent) || 0));
                        const unitAfter = (parseFloat(it.unit_price) || 0) * (1 - pct / 100);
                        return (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-slate-900">
                              <div className="text-slate-500 text-xs">{it.type}</div>
                              {it.description}
                            </td>
                            <td className="py-2 px-2 text-right">{it.quantity}</td>
                            <td className="py-2 px-2 text-right">{(parseFloat(it.unit_price) || 0).toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <input
                                type="number" min="0" max="100" step="0.1"
                                value={pct}
                                onChange={(e) => updateDiscount(idx, e.target.value)}
                                data-testid={`counter-item-discount-${idx}`}
                                className="w-16 px-1 py-1 text-xs border border-slate-300 rounded text-right"
                              />
                            </td>
                            <td className="py-2 px-2 text-right">{unitAfter.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right font-medium">{it.total_price.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <button type="button" onClick={() => removeItem(idx)} className="text-red-600" data-testid={`remove-counter-item-${idx}`}>×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-col items-end gap-1" data-testid="counter-totals-summary">
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>Total HT</span>
                      <span data-testid="counter-total-ht" className="font-medium">{totals.ht.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>TVA {(displayVat * 100).toFixed(1).replace('.0', '')}%</span>
                      <span data-testid="counter-total-vat" className="font-medium">{totals.vat.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between border-t border-slate-300 pt-1 mt-1 text-slate-900 font-bold text-lg">
                      <span>Total TTC</span>
                      <span data-testid="counter-total-ttc">{totalTTC.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mode de paiement</label>
                <Select value={saleForm.payment_method} onValueChange={(v) => setSaleForm({ ...saleForm, payment_method: v })}>
                  <SelectTrigger data-testid="counter-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="card">Carte bancaire</SelectItem>
                    <SelectItem value="transfer">Virement</SelectItem>
                    <SelectItem value="check">Chèque</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">💰 Un mouvement de caisse &laquo;&nbsp;Vente comptoir&nbsp;&raquo; sera créé automatiquement.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes internes (optionnel)</label>
                <textarea
                  value={saleForm.notes}
                  onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })}
                  data-testid="counter-notes-input"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                <Button
                  type="submit"
                  data-testid="submit-counter-sale-btn"
                  disabled={submitting || saleForm.items.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  <Receipt size={18} className="mr-2" />
                  {submitting ? 'Traitement…' : 'Encaisser & imprimer ticket'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CounterSales;
